import { Component, ViewChild } from '@angular/core';
import { PtLuxonDate } from '../../../../models/projects/date.models';
import { AppData } from '../../../../models/app-data/app-data';
import { NavController, NavParams } from 'ionic-angular';
import { LockedDayService } from '../../../../services/locked-day/locked-day.service';
import { DateService } from '../../../../services/date/date.service';
import { DailyLogService } from '../../../../services/daily-log/daily-log.service';
import { AppDataSyncService } from '../../../../services/app-data-sync/app-data-sync.service';
import { SetupService } from '../../../../services/setup/setup.service';
import { UpdatedSetupSummary } from '../../../../models/setup/setup.models';
import { Subject } from 'rxjs/Subject';
import { LockDayFilters } from '../../../../models/lock-day.models';
import { PageValidationType, ValidationResult } from '../../../../models/projects/validation.models';
import { ValidationService } from '../../../../services/validation/validation.service';
import { catchError, debounce, finalize } from 'rxjs/operators';
import { timer } from 'rxjs/observable/timer';
import { TimeZoneService } from '../../../../services/time-zone/time-zone.service';
import { DateParserService } from '../../../../services/daily-log/daily-log-time/date-parser-service';
import { DailyObservationPermissions, LockedDayPermissions } from '../../../../models/permissions/permission.models';
import { PermissionsService } from '../../../../services/permissions/permissions.service';
import { DailyObservationEditor } from '../../../../models/daily-observations/daily-observations';
import { DailyObservationService } from '../../../../services/daily-observation/daily-observation.service';
import { CompleteDayService } from '../../../../services/complete-day/complete-day.service';
import { DbContext, DbTable } from '../../../../services/data/dbcontext.service';
import { NavHelperService } from '../../../../services/nav/nav-helper.service';
import { MorningMinutesReportService } from '../../../../services/morning-minutes/morning-minutes-report.service';
import { cloneDeep } from 'lodash';
import { ImplementerService } from '../../../../services/implementer/implementer.service';
import { DailyLogDatePickerComponent } from '../../../shared/components/daily-log-date-picker/daily-log-date-picker';
import { AppConfig } from '../../../../app/app.config';

@Component({
  selector: 'app-daily-observation-edit-component',
  templateUrl: 'daily-observation-edit-component.component.html'
})
export class DailyObservationEditComponentComponent {
  @ViewChild('dailyLogDatePickerComponent') dailyLogDatePickerComponent: DailyLogDatePickerComponent;

  config = AppConfig.settings;

  dailyObservationTable: DbTable;
  dailyObservationEditor: DailyObservationEditor = new DailyObservationEditor();
  dailyLogDate: PtLuxonDate = new PtLuxonDate();
  validationResult: ValidationResult = new ValidationResult();
  dataToUpdate: AppData = new AppData();
  $dailyObservationConfirmedChange: Subject<DailyObservationEditor> = new Subject<DailyObservationEditor>();
  debounceActive = false;

  $validationConfirmedChange: Subject<boolean> = new Subject<boolean>();
  validationDebounceActive = false;

  loading: boolean;
  validationLoading: boolean;

  setupSummary: UpdatedSetupSummary;
  currentDate: PtLuxonDate = new PtLuxonDate();
  dayLockedByDate: boolean = false;
  lockable: boolean = false;
  dayCompleted: boolean;
  isImplementerActiveForDay: boolean; // when true the day will be open for editing regardless of dayLockedByDate

  permissions: DailyObservationPermissions = new DailyObservationPermissions();
  lockedDayPermissions: LockedDayPermissions = new LockedDayPermissions();
  currentSetupConfigured: boolean;

  firstLoadComplete = false;
  dateBeforeInitialSetup: boolean;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public lockedDayService: LockedDayService,
    public dateService: DateService,
    public dailyLogService: DailyLogService,
    private appDataSyncService: AppDataSyncService,
    public setupService: SetupService,
    private validationService: ValidationService,
    private timeZoneService: TimeZoneService,
    private dateParserService: DateParserService,
    private permissionService: PermissionsService,
    public dailyObservationService: DailyObservationService,
    private completeDayService: CompleteDayService,
    private navHelperService: NavHelperService,
    private dbContext: DbContext,
    private morningMinutesReportService: MorningMinutesReportService,
    private implementerService: ImplementerService,
  ) {
    this.dailyObservationTable = this.dbContext.tables.find(t => t.name === 'DailyObservationData');

    this.navHelperService.currentSetupConfigured.subscribe(currentSetupConfigured => {
      this.currentSetupConfigured = currentSetupConfigured;
    });
  }

  async ngOnInit() {
    this.loading = true;
    await this.ionViewEnter();
    await this.updateDataEntryVariables();

    this.$dailyObservationConfirmedChange
      .pipe(
        catchError(err => {
          this.debounceActive = false;
          throw (err.error);
        }),
        debounce(() => timer(600)))
      .subscribe(val => {
        this.saveEditorToLocalStorage();
        // note that resulting editor.date has not been deserialised here, as all we need is the id
        this.dailyObservationService.saveEditor(val)
          .then(res => {
            this.debounceActive = false;
            this.dailyObservationEditor.id = res;
          });
      });
  }

  async ionViewEnter() {
    this.validationLoading = true;
    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
    await this.loadData(true);
    this.setupValidationDebounce();

    this.permissions = await this.permissionService.dailyObservationPermissions();
    this.lockedDayPermissions = await this.permissionService.lockedDayPermissions(this.dailyLogDate);
  }

  setupValidationDebounce() {
    this.$validationConfirmedChange
      .pipe(
        catchError(err => {
          this.validationDebounceActive = false;
          throw (err.error);
        }),
        debounce(() => timer(500)))
      .subscribe(val => {
        this.validationLoading = true;
        this.updateDailyObservationData(val);
      });
  }

  // one method for all - that checks dayCompleted, implementer status, etc, and sets canEnterData flag
  async updateDataEntryVariables() {
    await Promise.all([
      this.checkImplementerActive(),
      this.checkDayLocked(),
      this.permissionService.updateLockedDayFunctionalPermissions(this.lockedDayPermissions, this.dailyLogDate)
    ]);

    await this.setCanEnterDataFlag();
  }

  async setCanEnterDataFlag() {
    if (!this.dayCompleted
      && ((this.config.isImplementerEnvironment && this.isImplementerActiveForDay) || (!this.config.isImplementerEnvironment && this.dayLockedByDate))) {
      this.navHelperService.updateCanEnterData(true);
    } else {
      this.navHelperService.updateCanEnterData(false);
    }
  }

  async checkDayLocked() {
    const filters: LockDayFilters = {
      date: this.dailyLogDate.dateTime,
      userId: null,
      history: null
    };

    const lockedSummary = await this.lockedDayService.checkDay(filters);

    this.dayLockedByDate = lockedSummary.lockedByDate;
    this.lockable = lockedSummary.lockable;

    this.completeDayService.checkDay(filters).then(result => {
      this.dayCompleted = result;
    });
  }

  async dayUnlockedForEditing() {
    this.loading = true;
    await this.checkDayLocked();
    this.loading = false;
  }

  async changesCancelled() {
    this.firstLoadComplete = false;
    this.loading = true;
    await this.loadData(true);
  }

  async changesSaved() {
    await this.loadData(false);
  }

  validationDebounceInvoker(isComplete: boolean) {
    this.$validationConfirmedChange.next(isComplete);
  }

  public async updateDailyObservationData(isComplete: boolean) {
    this.validationResult = new ValidationResult();

    if ((!this.config.isImplementerEnvironment && !this.dayLockedByDate)
      || ((this.config.isImplementerEnvironment && !this.isImplementerActiveForDay))) {
      return;
    }

    this.dailyObservationService.validate(this.dailyObservationEditor).pipe(
      finalize(async () => {
        await this.saveEditorToLocalStorage();
        await this.sendToDebounce(this.dailyObservationEditor);

        this.validateDailyObservations();
        this.loading = false;
      })).subscribe(async () => {
        this.dailyObservationEditor.isCompleted = isComplete;
      },
      error => {
        if (error.status && error.status === 412) {
          this.validationResult.errors = this.validationResult.errors.concat(
            error.error.errors.filter(p =>
              this.validationResult.errors.filter(er =>
                er.message === p.message).length === 0));
          this.validationResult.isValid = error.error.isValid;
          this.dailyObservationEditor.isCompleted = false;
        }
      });
  }

  markVesselCrewComplete() {
    this.updateDailyObservationData(true);
  }

  async sendToDebounce(row: DailyObservationEditor) {
    this.dataToUpdate.vesselCrewStats = this.dataToUpdate.vesselCrewStats
      .filter(
        p => p.id !== row.id &&
          !this.dateService.compareDayOffsetsPreApplied(p.date.dateTime, cloneDeep(row.date.dateTime)));

    const rowClone = cloneDeep(row);

    rowClone.date = cloneDeep(this.dailyLogDate);

    this.dataToUpdate.dailyObservationStats.push(rowClone);
    this.$dailyObservationConfirmedChange.next(rowClone);

    this.debounceActive = true;
  }

  async loadData(refresh: boolean, dateChange?: boolean) {
    if (refresh) {
      this.loading = true;
      this.dailyObservationEditor.date = this.dailyLogDate;
    }

    // refresh daily log date, if user has changed the date
    if (dateChange) {
      this.firstLoadComplete = false;
      this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
      await this.updateDataEntryVariables();
    }

    await this.checkDayLocked();

    if (refresh) {
      await this.appDataSyncService.updateAppDataForDate(this.dailyLogDate);

      this.validationResult = new ValidationResult();
      this.loading = true;

      this.dailyObservationService.getObservationDataForDay(this.dailyLogDate).then(data => {
        this.dailyObservationEditor = data;
        this.firstLoadComplete = true;
        this.loading = false;
        this.dailyObservationService.validate(this.dailyObservationEditor).subscribe(() => {
            this.validateDailyObservations();
          },
          error => {
            if (error.status && error.status === 412) {
              this.validationResult.errors = this.validationResult.errors.concat(
                error.error.errors.filter(p =>
                  this.validationResult.errors.filter(er =>
                    er.message === p.message).length === 0));
              this.validationResult.isValid = error.error.isValid;
              this.dailyObservationEditor.isCompleted = false;
            }
          });
      });
    }
  }

  otherPagesNotComplete(): boolean {
    return this.validationResult.errors.some(e => e.pageValidationType != null
      && e.pageValidationType !== PageValidationType.DailyObservations
      && e.pageValidationType !== PageValidationType.FieldPositioning
      && e.pageValidationType !== PageValidationType.VoyageLog);
  }

  updateMorningMinutesReportEntity() {
    this.morningMinutesReportService.updateDataModifiedFlag(this.dailyLogDate);
  }

  validateDailyObservations() {
    this.validationLoading = true;
    this.validationService.validateDailyObservationsOnPage(this.dailyLogDate, this.validationResult).then(result => {
      this.validationResult = result;
      this.validationLoading = false;
    });
  }

  // isImplementerEnvironment variable only tells us if the environment is the implementer, we still need to check if current date falls between
  // snapshotSummary start and end dates, if it does then we know this date is editable
  async checkImplementerActive() {
    this.isImplementerActiveForDay = await this.implementerService.implementerIsActive(this.config.isVesselEnvironment, cloneDeep(this.dailyLogDate));
  }

  dateBeforeInitialSetupChanged($event: boolean) {
    this.dateBeforeInitialSetup = $event;
  }

  goToActiveLogDay() {
    this.dailyLogDatePickerComponent.goToActiveLogDay();
  }

  dateLoadingStarted() {
    this.firstLoadComplete = false;
  }

  dateLoadingFinished() {
    this.firstLoadComplete = true;
  }

  async saveEditorToLocalStorage() {
    const dailyObservationEditorClone = cloneDeep(this.dailyObservationEditor);
    await this.dailyObservationService.saveDailyObservationChangeMultipleRecords([dailyObservationEditorClone]);
  }
}



// WEBPACK FOOTER //
// ./src/pages/DPR Pages/daily-observations/daily-observation-edit-component/daily-observation-edit-component.component.ts